import CallIcon from "@mui/icons-material/Call";
import FilterListIcon from "@mui/icons-material/FilterList";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import {
  BandBadge,
  BlockCard,
  SegmentedPills,
  SelectPill,
  TablePager,
} from "../primitives";
import type { PillOption } from "../primitives";
import { BandKey, BlockBaseProps, Delta, Option } from "../types";
import ReadingBySchoolTableSkeleton from "./Skeleton";
import styles from "./style.module.scss";

export interface SchoolRankRow {
  id: number | string;
  rank: number;
  name: string;
  campus: string;
  city: string;
  band: BandKey | string;
  /** Subscription status: "Active", "Expired" or "Paused". */
  status: string;
  hasInstallment: boolean;
  /** Already formatted, e.g. "9,842 min". */
  minutes: string;
  delta?: Delta;
  regular: number;
  regularPct: string;
  seatsUsed: number;
  seats: number;
  /** Days until renewal; negative means already past due. */
  lead: number;
  contact: string;
  role: string;
  phone: string;
  initials: string;
}

interface ReadingBySchoolTableProps extends BlockBaseProps {
  rows: SchoolRankRow[];
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  accountOptions?: Option[];
  accountFilter?: string;
  onAccountFilterChange?: (key: string) => void;
  statusFilters?: PillOption[];
  statusFilter?: string;
  onStatusFilterChange?: (key: string) => void;
  onSelectSchool?: (id: number | string) => void;
  title?: string;
}

const COLUMNS = [
  "#", "School", "Status", "Band", "Reading", "vs before",
  "Regular readers", "Seats used", "Lead time", "Who to call",
];

/** Every school ranked by reading, with the renewal context needed to act. */
function ReadingBySchoolTable({
  rows,
  page,
  pageSize,
  total,
  onPageChange,
  accountOptions,
  accountFilter,
  onAccountFilterChange,
  statusFilters,
  statusFilter,
  onStatusFilterChange,
  onSelectSchool,
  title = "Reading by school",
  loading,
  empty,
  className,
}: ReadingBySchoolTableProps) {
  if (loading) {
    return (
      <ReadingBySchoolTableSkeleton
        count={Math.min(pageSize, 8)}
        className={className}
      />
    );
  }

  /** Under two weeks is urgent, under a month is a warning. */
  const leadClass = (lead: number) =>
    lead <= 14 ? styles.leadUrgent : lead <= 30 ? styles.leadSoon : styles.leadOk;

  return (
    <BlockCard
      empty={empty}
      flush
      title={title}
      className={className}
      actions={
        <>
          {accountOptions && accountFilter && onAccountFilterChange && (
            <SelectPill
              options={accountOptions}
              value={accountFilter}
              onChange={onAccountFilterChange}
              icon={<FilterListIcon />}
              ariaLabel="Account type"
            />
          )}
          {statusFilters && statusFilter && onStatusFilterChange && (
            <SegmentedPills
              options={statusFilters}
              value={statusFilter}
              onChange={onStatusFilterChange}
              size="sm"
            />
          )}
        </>
      }
    >
      <div className={styles.scroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th key={column} className={styles.th}>
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => onSelectSchool?.(row.id)}
                className={styles.tr}
                data-clickable={onSelectSchool ? "true" : undefined}
              >
                <td className={`${styles.td} ${styles.rank}`}>{row.rank}</td>

                <td className={`${styles.td} ${styles.schoolCell}`}>
                  <div className={styles.schoolName}>{row.name}</div>
                  <div className={styles.schoolSub}>
                    {row.city ? `${row.campus} · ${row.city}` : row.campus}
                  </div>
                </td>

                <td className={styles.td}>
                  <div className={styles.statusCell}>
                    <BandBadge band={row.status} status />
                    {row.hasInstallment && (
                      <span
                        className={styles.installment}
                        title="This school pays its plan in installments"
                      >
                        <ReceiptLongIcon className={styles.installmentIcon} />
                        Installments
                      </span>
                    )}
                  </div>
                </td>

                <td className={styles.td}>
                  <BandBadge band={row.band} />
                </td>

                <td className={styles.td}>
                  <span className={styles.minutes}>{row.minutes}</span>
                </td>

                <td className={styles.td}>
                  {row.delta && (
                    <span className={styles.delta} data-tone={row.delta.tone}>
                      {row.delta.value}
                    </span>
                  )}
                </td>

                <td className={styles.td}>
                  {row.regular}{" "}
                  <span className={styles.muted}>{`(${row.regularPct})`}</span>
                </td>

                <td className={`${styles.td} ${styles.nowrap}`}>
                  {`${row.seatsUsed} / ${row.seats}`}
                </td>

                <td className={`${styles.td} ${styles.nowrap}`}>
                  <span className={leadClass(row.lead)}>{row.lead}</span> days
                </td>

                <td className={`${styles.td} ${styles.contactCell}`}>
                  <div className={styles.contact}>
                    <div className={styles.avatar}>{row.initials}</div>
                    <div className={styles.contactBody}>
                      <div className={styles.contactName}>
                        {row.contact}
                        <span className={styles.contactRole}>{` · ${row.role}`}</span>
                      </div>
                      <a
                        href={`tel:${row.phone}`}
                        onClick={(event) => event.stopPropagation()}
                        className={styles.phone}
                      >
                        <CallIcon className={styles.phoneIcon} />
                        {row.phone}
                      </a>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TablePager
        page={page}
        pageSize={pageSize}
        total={total}
        onChange={onPageChange}
      />
    </BlockCard>
  );
}

export default ReadingBySchoolTable;
